export type CapabilityTier = 'full' | 'send-only' | 'unsupported';

export interface CapabilityEnvironment {
  secureContext: boolean;
  webRtc: boolean;
  openFilePicker: boolean;
  directoryPicker: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

export interface DeviceCapabilities {
  tier: CapabilityTier;
  secureContext: boolean;
  webRtc: boolean;
  filePicker: boolean;
  directoryPicker: boolean;
  canConnect: boolean;
  canSendFiles: boolean;
  canSendFolders: boolean;
  canReceive: boolean;
  canResumeOutgoing: boolean;
  mobile: boolean;
  ios: boolean;
  android: boolean;
}

function browserEnvironment(): CapabilityEnvironment {
  return {
    secureContext: window.isSecureContext,
    webRtc: 'RTCPeerConnection' in window,
    openFilePicker: 'showOpenFilePicker' in window,
    directoryPicker: 'showDirectoryPicker' in window,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

export function detectDeviceCapabilities(environment = browserEnvironment()): DeviceCapabilities {
  const ios = /iPhone|iPad|iPod/iu.test(environment.userAgent)
    || (environment.platform === 'MacIntel' && environment.maxTouchPoints > 1);
  const android = /Android/iu.test(environment.userAgent);
  const mobile = ios || android || /Mobile/iu.test(environment.userAgent);
  const canConnect = environment.secureContext && environment.webRtc;
  const canReceive = canConnect && environment.directoryPicker;
  const canSendFiles = canConnect;
  const full = canReceive && environment.openFilePicker;
  return {
    tier: !canConnect ? 'unsupported' : full ? 'full' : 'send-only',
    secureContext: environment.secureContext,
    webRtc: environment.webRtc,
    filePicker: environment.openFilePicker,
    directoryPicker: environment.directoryPicker,
    canConnect,
    canSendFiles,
    canSendFolders: full,
    canReceive,
    canResumeOutgoing: full,
    mobile,
    ios,
    android,
  };
}

export function capabilityDescription(capabilities: DeviceCapabilities): string {
  if (!capabilities.secureContext) return 'Open this application over HTTPS to transfer files.';
  if (!capabilities.webRtc) return 'This browser cannot create a direct peer-to-peer connection.';
  if (capabilities.tier === 'send-only') {
    return capabilities.ios
      ? 'iPhone and iPad can send selected files, but cannot receive folders or large resumable transfers.'
      : 'This browser can send selected files, but direct-to-folder receiving is unavailable.';
  }
  if (capabilities.android) return 'Full Android mode: send and receive while keeping this app visible.';
  return 'Full transfer mode: send and receive files and folders with resumable direct-to-disk writing.';
}
